import { defineStore } from 'pinia'
import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000/api'

const userApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

userApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

userApi.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response) {
      const errorMessage =
        error.response.data?.message || error.response.data?.error || 'An error occurred'

      const customError = new Error(errorMessage)
      customError.status = error.response.status
      customError.data = error.response.data

      return Promise.reject(customError)
    }

    return Promise.reject(new Error('Network error'))
  },
)

export const useUserStore = defineStore('user', {
  state: () => ({
    users: [],
    loading: false,
    error: null,
    pagination: {
      page: 1,
      perPage: 10,
      total: 0,
      totalPages: 0,
    },
    filters: {
      role: '',
      status: '',
      search: '',
    },
  }),

  getters: {
    filteredUsers: (state) => {
      let filtered = [...state.users]

      if (state.filters.role) {
        filtered = filtered.filter((user) => user.role === state.filters.role)
      }

      if (state.filters.status) {
        const isActive = state.filters.status === 'active'
        filtered = filtered.filter((user) => user.is_active === isActive)
      }

      if (state.filters.search) {
        const search = state.filters.search.toLowerCase()
        filtered = filtered.filter(
          (user) =>
            user.name.toLowerCase().includes(search) || user.email.toLowerCase().includes(search),
        )
      }

      return filtered
    },

    totalUsers: (state) => state.users.length,
    activeUsers: (state) => state.users.filter((user) => user.is_active).length,
    inactiveUsers: (state) => state.users.filter((user) => !user.is_active).length,
  },

  actions: {
    async fetchUsers(page = 1, perPage = 10) {
      this.loading = true
      this.error = null

      try {
        const params = new URLSearchParams({
          page: page.toString(),
          per_page: perPage.toString(),
          ...this.filters,
        })

        const response = await userApi.get(`/users?${params}`)

        // Handle Laravel pagination response structure
        this.users = response.data || []
        this.pagination = {
          page: response.meta?.current_page || page,
          perPage: response.meta?.per_page || perPage,
          total: response.meta?.total || this.users.length,
          totalPages: response.meta?.last_page || Math.ceil(this.users.length / perPage),
        }

        return { success: true }
      } catch (error) {
        this.error = error.message || 'Failed to fetch users'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async createUser(userData) {
      this.loading = true
      this.error = null

      try {
        const response = await userApi.post('/users', userData)

        // Add new user to the beginning of the list
        const newUser = response.data || response
        this.users.unshift(newUser)
        return { success: true, user: newUser }
      } catch (error) {
        this.error = error.message || 'Failed to create user'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async updateUser(userId, userData) {
      this.loading = true
      this.error = null

      try {
        const response = await userApi.put(`/users/${userId}`, userData)

        const updatedUser = response.data || response
        const index = this.users.findIndex((user) => user.id === userId)

        if (index !== -1) {
          this.users[index] = updatedUser
        }

        return { success: true, user: updatedUser }
      } catch (error) {
        this.error = error.message || 'Failed to update user'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async deactivateUser(userId) {
      this.loading = true
      this.error = null

      try {
        const response = await userApi.delete(`/users/${userId}`)

        const index = this.users.findIndex((user) => user.id === userId)

        if (index !== -1) {
          this.users[index].is_active = false
        }

        return { success: true }
      } catch (error) {
        this.error = error.message || 'Failed to deactivate user'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async activateUser(userId) {
      this.loading = true
      this.error = null

      try {
        const response = await userApi.patch(`/users/${userId}/activate`)

        const index = this.users.findIndex((user) => user.id === userId)

        if (index !== -1) {
          this.users[index].is_active = true
        }

        return { success: true }
      } catch (error) {
        this.error = error.message || 'Failed to activate user'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    setFilters(filters) {
      this.filters = { ...this.filters, ...filters }
    },

    clearFilters() {
      this.filters = {
        role: '',
        status: '',
        search: '',
      }
    },

    clearError() {
      this.error = null
    },
  },
})
